import { useEffect, useState } from 'react';

/**
 * `value`, held back until it has stopped changing for `delayMs`.
 *
 * Used for the outgoing half of a search box: the input itself stays instant
 * (it is driven from the URL), while the request that costs the server a query
 * waits for the operator to finish the word.
 *
 * The first value is returned immediately rather than after one delay — a list
 * that waited 300ms before its own first fetch would fire two requests on every
 * page load and show a skeleton for no reason.
 *
 * `setState` here runs inside the timer callback, never synchronously in the
 * effect body: React Compiler's `set-state-in-effect` rule is on at error level
 * in this workspace, and it is right — a synchronous set would be a second
 * render pass for something that could have been derived.
 */
export function useDebouncedValue<TValue>(value: TValue, delayMs = 300): TValue {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (Object.is(value, debounced)) {
      // Typed a character and deleted it: nothing to commit, and scheduling a
      // timer anyway would keep re-running this effect forever.
      return;
    }

    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);

    return () => {
      clearTimeout(timer);
    };
  }, [value, debounced, delayMs]);

  return debounced;
}
